import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pill, Activity, Trash2, Calendar, AlertTriangle, Sun, Sunset, Moon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

interface ActivityRecord {
  id: string;
  patient_id: string;
  timestamp: string;
  category: string;
  activity_type: string;
  description: string;
  medication_name?: string;
  dosage?: string;
  systolic?: number;
  diastolic?: number;
  heart_rate?: number;
  input_method: string;
  recorded_by: string;
  notes?: string;
}

interface ActivityTimelineProps {
  activities: ActivityRecord[];
  onDelete?: (activityId: string) => void;
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ activities, onDelete }) => {
  const { t } = useTranslation();

  // Group activities by date and time interval
  const groupedActivities = React.useMemo(() => {
    const groups: { [dateKey: string]: { [intervalKey: string]: ActivityRecord[] } } = {};
    
    activities.forEach(activity => {
      const date = new Date(activity.timestamp);
      const dateKey = date.toISOString().split('T')[0];
      const hour = date.getHours();
      
      let intervalKey = 'evening';
      if (hour >= 6 && hour < 12) {
        intervalKey = 'morning';
      } else if (hour >= 12 && hour < 18) {
        intervalKey = 'afternoon';
      }
      
      if (!groups[dateKey]) {
        groups[dateKey] = { morning: [], afternoon: [], evening: [] };
      }
      groups[dateKey][intervalKey].push(activity);
    });
    
    return groups;
  }, [activities]);

  const getDateLabel = (dateKey: string) => {
    const date = new Date(dateKey);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    
    if (date.getTime() === today.getTime()) {
      return t('patientActivity.timelineView.today');
    } else if (date.getTime() === yesterday.getTime()) {
      return t('patientActivity.timelineView.yesterday');
    } else {
      return date.toLocaleDateString('zh-CN', { month: 'long', day: 'numeric', weekday: 'short' });
    }
  };

  const getIntervalIcon = (interval: string) => {
    switch (interval) {
      case 'morning':
        return <Sun className="h-4 w-4 text-orange-500" />;
      case 'afternoon':
        return <Sunset className="h-4 w-4 text-amber-600" />;
      case 'evening':
        return <Moon className="h-4 w-4 text-indigo-400" />;
      default:
        return null;
    }
  };

  const getCategoryIcon = (category: string, activityType: string) => {
    if (category === 'medication') {
      return <Pill className="h-4 w-4 text-blue-500" />;
    } else if (category === 'vital_signs' && activityType === 'bp_reading') {
      return <Activity className="h-4 w-4 text-red-500" />;
    }
    return <Calendar className="h-4 w-4 text-gray-500" />;
  };

  const isAbnormal = (activity: ActivityRecord) => {
    if (activity.category === 'vital_signs' && activity.activity_type === 'bp_reading') {
      if (activity.systolic && activity.systolic > 140) return true;
      if (activity.diastolic && activity.diastolic > 90) return true;
    }
    return false;
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  };

  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Calendar className="h-16 w-16 text-gray-300 mb-4" />
        <p className="text-gray-500 text-lg font-medium">{t('patientActivity.noActivities')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <AnimatePresence>
        {Object.keys(groupedActivities)
          .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
          .map(dateKey => (
            <motion.div
              key={dateKey}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative"
            >
              {/* Date Header */}
              <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm py-2 mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-gray-900 px-1">
                    {getDateLabel(dateKey)}
                  </span>
                  <div className="flex-1 h-px bg-gray-100"></div>
                </div>
              </div>

              {/* Day Intervals */}
              <div className="space-y-8 ml-2">
                {['morning', 'afternoon', 'evening'].map(interval => {
                  const intervalActivities = groupedActivities[dateKey][interval];
                  if (!intervalActivities || intervalActivities.length === 0) return null;

                  return (
                    <div key={interval} className="relative pl-8 border-l-2 border-dashed border-gray-100 last:border-0">
                      {/* Interval Marker */}
                      <div className="absolute -left-[11px] top-0 flex items-center justify-center w-5 h-5 rounded-full bg-white border-2 border-gray-100 shadow-sm">
                        {getIntervalIcon(interval)}
                      </div>

                      {/* Interval Label */}
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                          {t(`patientActivity.timelineView.${interval}`)}
                        </span>
                        <span className="text-[10px] text-gray-300 px-1.5 py-0.5 rounded-full bg-gray-50 border border-gray-100">
                          {intervalActivities.length} {t('patientActivity.units.times')}
                        </span>
                      </div>

                      {/* Interval Activities */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {intervalActivities
                          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                          .map((activity, index) => (
                            <motion.div
                              key={activity.id}
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <Card className={`group relative bg-white border border-gray-100 hover:border-indigo-100 hover:shadow-md hover:shadow-indigo-50/50 transition-all duration-300 rounded-2xl overflow-hidden ${isAbnormal(activity) ? 'ring-1 ring-red-100 bg-red-50/30' : ''}`}>
                                <CardContent className="p-4">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-2">
                                        <div className="p-1.5 rounded-lg bg-gray-50 group-hover:bg-white transition-colors">
                                          {getCategoryIcon(activity.category, activity.activity_type)}
                                        </div>
                                        <span className="text-[10px] font-bold text-gray-400">
                                          {formatTime(activity.timestamp)}
                                        </span>
                                        {isAbnormal(activity) && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger>
                                                <div className="flex items-center animate-pulse">
                                                  <AlertTriangle className="h-4 w-4 text-red-500 fill-red-50" />
                                                </div>
                                              </TooltipTrigger>
                                              <TooltipContent className="bg-red-600 text-white border-none rounded-lg text-xs font-bold">
                                                数据异常，请密切关注
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                      </div>

                                      <h4 className="text-sm font-bold text-gray-900 truncate mb-1">
                                        {activity.description}
                                      </h4>

                                      {/* Specific details in a compact way */}
                                      <div className="flex flex-wrap gap-2">
                                        {activity.category === 'medication' && activity.medication_name && (
                                          <Badge variant="outline" className="h-5 px-1.5 text-[10px] bg-blue-50/50 text-blue-600 border-blue-100 font-bold">
                                            💊 {activity.medication_name} {activity.dosage}
                                          </Badge>
                                        )}
                                        
                                        {activity.category === 'vital_signs' && activity.activity_type === 'bp_reading' && activity.systolic && (
                                          <Badge variant="outline" className={`h-5 px-1.5 text-[10px] font-bold border-red-100 ${isAbnormal(activity) ? 'bg-red-500 text-white border-transparent' : 'bg-red-50/50 text-red-600'}`}>
                                            {activity.systolic}/{activity.diastolic} {t('patientActivity.units.mmHg')}
                                          </Badge>
                                        )}
                                        
                                        {activity.heart_rate && (
                                          <Badge variant="outline" className="h-5 px-1.5 text-[10px] bg-pink-50/50 text-pink-600 border-pink-100 font-bold">
                                            ❤️ {activity.heart_rate} {t('patientActivity.units.bpm')}
                                          </Badge>
                                        )}
                                      </div>
                                    </div>

                                    {/* Action Button - Hidden by default, shows on hover */}
                                    {onDelete && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => onDelete(activity.id)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-300 hover:text-red-500 hover:bg-red-50 h-7 w-7 rounded-full"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    )}
                                  </div>

                                  {activity.notes && (
                                    <div className="mt-2 pt-2 border-t border-gray-50">
                                      <p className="text-[11px] text-gray-500 line-clamp-1 italic">
                                        “{activity.notes}”
                                      </p>
                                    </div>
                                  )}
                                </CardContent>
                              </Card>
                            </motion.div>
                          ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
      </AnimatePresence>
    </div>
  );
};

export default ActivityTimeline;
